// The IndexedDB layer under `vault-key-store.ts`: the record shape, the
// error type, the SSR guard, and the raw read/write/delete operations.
//
// Split out of `vault-key-store.ts` so that file can be about the key and
// this one about the database. The division is a real one: nothing in
// here knows what an X25519 keypair is, and nothing in
// `vault-key-store.ts` touches a transaction. Callers import from
// `vault-key-store.ts`, which re-exports the public names below; this
// module is an implementation detail of that one.
//
// **What is stored is a `CryptoKey`, not bytes.** IndexedDB values go
// through the structured clone algorithm, which handles `CryptoKey`
// natively and — crucially — preserves `extractable: false` across the
// round trip. The browser keeps the key material on its side of the
// WebCrypto boundary; what comes back out is a handle that script can
// USE (ECDH, and therefore `open()`) and can never READ. That is the
// whole storage design, and it is why there is no ciphertext, no nonce
// and no wrapping key anywhere in this file: there is nothing left to
// encrypt, because we never hold the material to begin with.
//
// SECURITY: nothing in this file logs, prints, or puts into an error
// message any key material. The only values that reach a message are
// public metadata — a root id, an algorithm name, a key type.

const DB_NAME = 'stoaria-vault-keys'

/** Bumped from 1 when the record stopped being `AES-GCM(PRF-derived key,
 * PKCS8)` and became a bare `CryptoKey` handle.
 *
 * The upgrade DROPS the old object store rather than migrating it, and
 * that is deliberate on two counts. A v1 record cannot be converted —
 * reading it needs the operator's passkey, which an upgrade transaction
 * cannot prompt for. And leaving it would mean the encrypted PKCS8 of
 * the tenant key sits in this origin's IndexedDB indefinitely, long
 * after the design that justified it was retired. Dropping costs one
 * recovery-file paste per browser, once; keeping costs a copy of the
 * wrapped tenant key on every machine that ever ran the old build. */
const DB_VERSION = 2

const STORE_NAME = 'tenant-keys'
const ROOT_INDEX = 'by-root'

export type VaultKeyStoreReason =
  /** No IndexedDB in this context: SSR on a Worker, or a browser profile
   * that blocks storage. Nothing is stored and nothing can be. */
  | 'unavailable'
  /** No record for this root AND this tenant public key. Includes the
   * rotated-key case, deliberately — see `vault-key-store.ts`. */
  | 'not-found'
  /** A record is present but is not a usable X25519 private handle: the
   * wrong algorithm, an extractable key, a missing `deriveBits` usage,
   * or a tenant public key that is not 32 bytes. Structurally wrong, as
   * opposed to cryptographically wrong — that is `key-mismatch`. */
  | 'corrupt'
  /** The stored handle is structurally fine but is NOT the private half
   * of the `tenantPubkey` stored beside it. The load-time self-test in
   * `vault-key-store.ts` exists to catch exactly this; read its comment
   * for what it is defending against and why it is the replacement for
   * the AES-GCM AAD the old record shape had. */
  | 'key-mismatch'
  | 'write-failed'

/** Every failure the vault key store raises. `reason` is the switchable
 * part; `message` is for the operator and never contains key material. */
export class VaultKeyStoreError extends Error {
  readonly reason: VaultKeyStoreReason

  constructor(
    reason: VaultKeyStoreReason,
    message: string,
    options?: ErrorOptions,
  ) {
    super(message, options)
    this.name = 'VaultKeyStoreError'
    this.reason = reason
  }
}

/** The on-disk record. Exported only so `vault-key-store.ts` can build
 * one; it is not part of the module's public surface.
 *
 * Three fields, and the absence of a fourth is the point — there is no
 * `method` discriminant, because there is only one way a key is stored
 * now. */
export interface StoredKeyRecord {
  rootId: string
  /** base64url raw X25519 public key, as the server reports it. Half of
   * the record's identity, and the public half the load-time self-test
   * checks `key` against. */
  tenantPubkey: string
  /** The tenant PRIVATE key as a non-extractable WebCrypto handle,
   * written and read back by structured clone. */
  key: CryptoKey
  storedAt: number
}

/** The SSR guard, and the only place `indexedDB` is touched. Returns null
 * on a Worker (no `indexedDB` global at all) and in any browser context
 * where the property exists but access throws — Safari in private mode
 * historically did exactly that. */
export function indexedDbOrNull(): IDBFactory | null {
  try {
    if (typeof globalThis.indexedDB === 'undefined') return null
    return globalThis.indexedDB
  } catch {
    // Not swallowing a real error: a throwing property access *is* the
    // "no IndexedDB here" answer, and this function's contract is to
    // report that as null.
    return null
  }
}

function awaitRequest<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = (): void => {
      resolve(req.result)
    }
    req.onerror = (): void => {
      reject(
        new VaultKeyStoreError(
          'write-failed',
          'the vault key store rejected a request',
          { cause: req.error ?? undefined },
        ),
      )
    }
  })
}

/** Opens (and on first use creates) the database. Resolves null when
 * there is no IndexedDB, so every caller has exactly one branch to take
 * for the SSR/unavailable case.
 *
 * Opened lazily, per call, never at module load: this app server-renders
 * on Cloudflare Workers and touching `indexedDB` at import time would
 * break SSR for every route that transitively imports it. */
function openDb(): Promise<IDBDatabase | null> {
  const factory = indexedDbOrNull()
  if (factory === null) return Promise.resolve(null)
  return new Promise((resolve, reject) => {
    const req = factory.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = (): void => {
      const db = req.result
      // Drop first: see DB_VERSION for why a v1 record is deleted rather
      // than migrated. Creating over an existing store is an error, so
      // this is also what makes the upgrade re-runnable.
      if (db.objectStoreNames.contains(STORE_NAME))
        db.deleteObjectStore(STORE_NAME)
      // Composite primary key: a record is identified by the root id AND
      // the tenant public key, so a rotated key cannot silently match.
      const objectStore = db.createObjectStore(STORE_NAME, {
        keyPath: ['rootId', 'tenantPubkey'],
      })
      // `forget(rootId)` drops every key ever stored for a root, across
      // rotations, which is what a "forget this device" button must do.
      objectStore.createIndex(ROOT_INDEX, 'rootId', { unique: false })
    }
    req.onsuccess = (): void => {
      resolve(req.result)
    }
    req.onerror = (): void => {
      reject(
        new VaultKeyStoreError(
          'unavailable',
          'could not open the vault key store',
          { cause: req.error ?? undefined },
        ),
      )
    }
  })
}

export async function readRecord(
  rootId: string,
  tenantPubkey: string,
): Promise<StoredKeyRecord | null> {
  const db = await openDb()
  if (db === null) return null
  try {
    const tx = db.transaction(STORE_NAME, 'readonly')
    const found: unknown = await awaitRequest(
      tx.objectStore(STORE_NAME).get([rootId, tenantPubkey]),
    )
    return parseStoredKeyRecord(found)
  } finally {
    db.close()
  }
}

export async function putRecord(record: StoredKeyRecord): Promise<void> {
  const db = await openDb()
  if (db === null)
    throw new VaultKeyStoreError(
      'unavailable',
      'the vault key store is not available in this context',
    )
  try {
    const tx = db.transaction(STORE_NAME, 'readwrite')
    await awaitRequest(tx.objectStore(STORE_NAME).put(record))
  } finally {
    db.close()
  }
}

/** Drops one record. Used by the load-time self-test to discard a handle
 * that does not match the public key stored beside it: a record that
 * cannot be trusted must not survive to be offered again on the next
 * page load, and re-offering a one-gesture unlock that is guaranteed to
 * fail is worse than offering the recovery-file paste that works. */
export async function deleteRecord(
  rootId: string,
  tenantPubkey: string,
): Promise<void> {
  const db = await openDb()
  if (db === null) return
  try {
    const tx = db.transaction(STORE_NAME, 'readwrite')
    await awaitRequest(
      tx.objectStore(STORE_NAME).delete([rootId, tenantPubkey]),
    )
  } finally {
    db.close()
  }
}

/** How many records this root has on this device, across rotations.
 *
 * Exists so the "Forget this device" readout can describe exactly what
 * the button deletes. `readRecord` is keyed on (rootId, tenantPubkey),
 * which is right for unlocking — you want THIS tenant key, not a stale
 * one — but `forget` works on the root index. Reporting the narrow probe
 * next to the broad delete produced a real dead end: a browser holding
 * only a record from before a rotation showed "Not remembered" and
 * offered no button, so the one record the operator most wanted gone was
 * the one the UI claimed was not there.
 *
 * Zero — never a throw — when IndexedDB is absent, matching `forget`'s
 * own no-op-when-absent contract.
 */
export async function countRecordsForRoot(rootId: string): Promise<number> {
  const db = await openDb()
  if (db === null) return 0
  try {
    const tx = db.transaction(STORE_NAME, 'readonly')
    const keys = await awaitRequest(
      tx.objectStore(STORE_NAME).index(ROOT_INDEX).getAllKeys(rootId),
    )
    return keys.length
  } finally {
    db.close()
  }
}

/** Drops every stored key for a vault root — across rotations, so
 * "forget this vault on this device" leaves nothing behind. A no-op, not
 * an error, when IndexedDB is absent or nothing is stored: forgetting
 * something that is not there has already succeeded. */
export async function forget(rootId: string): Promise<void> {
  const db = await openDb()
  if (db === null) return
  try {
    const tx = db.transaction(STORE_NAME, 'readwrite')
    const objectStore = tx.objectStore(STORE_NAME)
    const keys = await awaitRequest(
      objectStore.index(ROOT_INDEX).getAllKeys(rootId),
    )
    for (const key of keys) await awaitRequest(objectStore.delete(key))
  } finally {
    db.close()
  }
}

/** Clears the whole store — the "sign out of this browser" hammer. Same
 * no-op-when-absent contract as `forget`. */
export async function forgetAll(): Promise<void> {
  const db = await openDb()
  if (db === null) return
  try {
    const tx = db.transaction(STORE_NAME, 'readwrite')
    await awaitRequest(tx.objectStore(STORE_NAME).clear())
  } finally {
    db.close()
  }
}

/** Whether `value` looks like a WebCrypto `CryptoKey`.
 *
 * `value instanceof CryptoKey` is NOT usable here, for the same reason
 * `instanceof Uint8Array` was not usable on the old record's byte
 * fields. Structured clone hands back an object built from the
 * *platform's* constructor, which is not necessarily the one bound to
 * this realm's global — under jsdom the key is a Node WebCrypto key
 * while `globalThis.CryptoKey` is jsdom's (or absent), and `instanceof`
 * then reports false on a perfectly good handle. A Worker or an iframe
 * can produce the same mismatch.
 *
 * So this checks the shape instead. It is a weak check on purpose: its
 * job is only to reject obvious junk before the real gates in
 * `vault-key-store.ts` (algorithm, type, usages, and above all the
 * round-trip self-test), which are the ones that actually decide whether
 * the handle may be used. */
function isCryptoKeyLike(value: unknown): value is CryptoKey {
  if (typeof value !== 'object' || value === null) return false
  const candidate = value as {
    type?: unknown
    extractable?: unknown
    algorithm?: unknown
    usages?: unknown
  }
  return (
    typeof candidate.type === 'string' &&
    typeof candidate.extractable === 'boolean' &&
    typeof candidate.algorithm === 'object' &&
    candidate.algorithm !== null &&
    Array.isArray(candidate.usages)
  )
}

/** Validates what came back out of IndexedDB. The store is schema-free,
 * so a record written by an older version of this file (or by a
 * half-finished upgrade) must read as "no stored key" rather than
 * crashing the unlock screen. */
function parseStoredKeyRecord(value: unknown): StoredKeyRecord | null {
  if (typeof value !== 'object' || value === null) return null
  const candidate = value as Record<string, unknown>
  const { rootId, tenantPubkey } = candidate
  if (
    typeof rootId !== 'string' ||
    typeof tenantPubkey !== 'string' ||
    !isCryptoKeyLike(candidate.key)
  )
    return null
  return {
    rootId,
    tenantPubkey,
    key: candidate.key,
    storedAt: typeof candidate.storedAt === 'number' ? candidate.storedAt : 0,
  }
}
